import axios, { AxiosError, AxiosResponse } from "axios";

import { LogService, SearchService, SnackbarService } from "@/services";
import store, { ApplicationModule, getStoreModule, SessionModule } from "@/store";
import {
  AppConfigNestedNumberResponse,
  AppConfigNumberResponse,
  AppConfigResponse,
  Banner,
  BannerResponseItem,
  BriefcaseActions,
  BriefcaseFolder,
  BriefcaseFolderDownloadCheckResponse,
  BriefcaseFolderResponse,
  BriefcaseHardcodedIds,
  BriefcaseItem,
  BriefcaseResponse,
  BriefcaseResponseFolder,
  BriefcaseResponseItem,
  BriefcaseToggleRequest,
  CaucusCategoryResponse,
  CaucusResponse,
  CommitteeResponse,
  DateHelper,
  EmailVerificationResponse,
  FecData,
  FecDataResponse,
  HidePaywalledProfilesPreferenceResponse,
  HomepageConfigResponse,
  LocationResponse,
  LookupTableItem,
  LookupTables,
  LookupTablesKeys,
  NestedLookupTableItem,
  NestedLookupTables,
  NoteworthyGroup,
  NoteworthyGroupResponseItem,
  Profile,
  ProfileResponse,
  SalesforcePermission,
  SearchHelpers,
  SearchItem,
  SearchOrder,
  SearchQuery,
  SearchQueryStrings,
  SearchResponse,
  SearchResponseItem,
  SearchResultsPagination,
  SessionResponse,
  SubCommitteeResponse,
  SubscriptionModule,
  SubscriptionModuleResponse,
  TrialSignupResponse,
  User,
  UserContactResponse,
  UserManagementResponse,
  UserType,
  VignetteEndpoints,
  VignetteQueryString,
  VignetteQueryStrings,
} from "@/types";

/**
 * Singleton service that handles all API calls and updates Store
 *
 */
export class ApiService {
  static applicationStore: ApplicationModule;
  static sessionStore: SessionModule;

  private static _initialized = false;

  static _initialize(): void {
    if (this._initialized) {
      LogService.error("ApiService is already initialized.");
      return;
    }
    LogService.debug("Initializing ApiService.");

    this.applicationStore = getStoreModule(ApplicationModule, store);
    this.sessionStore = getStoreModule(SessionModule, store);

    axios.defaults.headers.common = {
      "Cache-Control": "no-cache",
      Pragma: "no-cache",
      Expires: "0",
    };

    this._initialized = true;
    LogService.info("ApiService is initialized.");
  }

  private static _getQueryStrings(queryStrings: VignetteQueryString[]): string {
    let queryStringParsed = "?";
    let count = 0;

    for (const queryString of queryStrings) {
      if (count > 0) queryStringParsed += "&";
      queryStringParsed += `${queryString.queryString}=${encodeURIComponent(queryString.value)}`;
      count++;
    }

    LogService.debug(`queryString created: '${queryStringParsed}'`);
    return queryStringParsed;
  }

  private static _getApiEndpoint(
    endpoint: VignetteEndpoints,
    parameter?: string,
    queryStrings?: VignetteQueryString[] | null
  ): string {
    let queryStringParsed = "";
    let parameterParsed = "";

    if (parameter != null && parameter !== "") {
      parameterParsed = parameter + "/";
    }

    if (queryStrings != null && queryStrings.length > 0) {
      queryStringParsed = this._getQueryStrings(queryStrings);
    }

    const result: string = this.applicationStore.getBaseUrlOrigin + endpoint + parameterParsed + queryStringParsed;
    LogService.debug(`ApiEndpoint created: '${result}'`);

    return result;
  }

  static generateSearchExportUrl(fileFormat: string): string {
    const queryStrings: VignetteQueryString[] = this._getSearchQueries();
    queryStrings.push({
      queryString: "file_format",
      value: fileFormat,
    });
    return this._getApiEndpoint(VignetteEndpoints.API_SEARCH_EXPORT, undefined, queryStrings);
  }

  static async addAllSearchResults(folderId: number): Promise<number[]> {
    const queryStrings: VignetteQueryString[] = this._getSearchQueries();
    queryStrings.push({
      queryString: "folder_id",
      value: folderId.toString(),
    });
    return new Promise<number[]>(async (resolve) => {
      axios
        .put(this._getApiEndpoint(VignetteEndpoints.API_ADD_ALL_SEARCH_RESULTS, undefined, queryStrings))
        .then(async (response: AxiosResponse) => {
          const success = !response.data.error_message;
          if (!success) {
            resolve(response.data.profile_ids);
          }
          resolve([]);
        })
        .catch((error: AxiosError) => {
          const response = error.response;

          // Log errors - Network error / CORS issue / backend error
          LogService.error(`addAllSearchResults response is ${response?.status || "null"}`);
          resolve([]);
        });
    });
  }

  static async refreshProfilesViewedDuringTrialItems(): Promise<boolean> {
    return new Promise<boolean>(async (resolve) => {
      const user = this.sessionStore.getUser;
      const profileIds = user?.profilesViewedDuringTrial;

      if (profileIds !== undefined && profileIds.length > 0) {
        const results = await ApiService.getSearchProfiles(profileIds);
        if (results !== undefined) {
          this.sessionStore.setProfilesViewedDuringTrialItems(results);
        }
      }
      resolve(true);
    });
  }

  static async refreshFeaturedProfiles(forceUpdate = false): Promise<boolean> {
    return new Promise<boolean>(async (resolve) => {
      const lastUpdated = this.applicationStore.getHomepageConfigLastUpdated;
      const isAlreadyLoading = this.applicationStore.isHomepageFeaturedProfilesLoading;
      const now = new Date();

      // Get new profiles only if it's been more than 5 minutes
      if (!isAlreadyLoading && (forceUpdate || lastUpdated == null || now.getTime() - lastUpdated.getTime() > 300000)) {
        LogService.info("Updating featured profiles.");

        // Clean up old profiles and refresh profiles on view load.
        this.applicationStore.clearHomepageFeaturedProfiles();
        this.applicationStore.setHomepageFeaturedProfilesLoading(true);

        await ApiService.getHomepageConfig();

        this.applicationStore.setHomepageFeaturedProfilesLoading(false);
        this.applicationStore.setHomepageFeaturedProfilesLastUpdated(new Date());
      } else if (isAlreadyLoading) {
        LogService.info(`Skipping Featured Profiles update: isAlreadyLoading`);
      } else {
        LogService.info(
          `Skipping Featured Profiles update: lastUpdated: ${lastUpdated ? lastUpdated.getTime() : "null"}, diff: ${
            lastUpdated ? now.getTime() - lastUpdated.getTime() : "null"
          }`
        );
      }
      resolve(true);
    });
  }

  // TODO: validate query
  static async getSearchProfiles(ids: number[]): Promise<SearchItem[] | undefined> {
    let result: SearchItem[] | undefined = undefined;
    const idsStr = ids.join();
    return new Promise<SearchItem[] | undefined>((resolve) => {
      axios
        .get<SearchResponseItem[]>(this._getApiEndpoint(VignetteEndpoints.API_SEARCH, idsStr))
        .then((response: AxiosResponse<SearchResponseItem[]>) => {
          // LogService.debug(`getSearchProfiles response: '${JSON.stringify(response.data)}'`);
          result = ApiService.parseSearchResponseList(response.data);
        })
        .catch((error: AxiosError<SearchResponseItem[]>) => {
          const response = error.response;

          // Log errors - Network error / CORS issue / backend error
          LogService.error(`getSearchProfiles response is ${response?.status || "null"}`);
        })
        .then(() => {
          LogService.info("search request complete.");
          resolve(result);
        });
    });
  }

  static async getHomepageConfig(): Promise<void> {
    return new Promise<void>((resolve) => {
      axios
        .get<HomepageConfigResponse>(this._getApiEndpoint(VignetteEndpoints.API_HOMEPAGE_CONFIG))
        .then((response: AxiosResponse<HomepageConfigResponse>) => {
          // Featured Profiles

          // featured_profiles_text

          if (!!response.data && !!response.data.featured_profiles && Array.isArray(response.data.featured_profiles)) {
            this.applicationStore.setFeaturedProfilesText(response.data.featured_profiles_text);

            for (const searchResponse of response.data.featured_profiles) {
              const searchItem: SearchItem = ApiService.parseSearchResponseItem(searchResponse);
              if (searchItem != undefined) this.applicationStore.addToHomepageFeaturedProfiles(searchItem);
            }
          } else {
            LogService.warn(
              `getHomepageConfig invalid featured profile list, endpoint returned: '${JSON.stringify(response.data)}'`
            );
          }

          // Recent Profiles
          if (!!response.data && !!response.data.recent_profiles && Array.isArray(response.data.recent_profiles)) {
            for (const searchResponse of response.data.recent_profiles) {
              const searchItem: SearchItem = ApiService.parseSearchResponseItem(searchResponse);
              if (searchItem != undefined) this.applicationStore.addToHomepageRecentProfiles(searchItem);
            }
          } else {
            LogService.warn(
              `getHomepageConfig invalid recent profile list, endpoint returned: '${JSON.stringify(response.data)}'`
            );
          }

          // Noteworthy Groups
          if (!!response.data && !!response.data.noteworthy_groups && Array.isArray(response.data.noteworthy_groups)) {
            for (const responseItem of response.data.noteworthy_groups) {
              const group: NoteworthyGroup = ApiService.parseNoteworthyGroupResponseItem(responseItem);
              if (group != undefined) this.applicationStore.addToHomepageNoteworthyGroups(group);
            }
          } else {
            LogService.warn(
              `getHomepageConfig invalid noteworthy groups list, endpoint returned: '${JSON.stringify(response.data)}'`
            );
          }

          if (!!response.data && !!response.data.primary_banner) {
            const banner: Banner = ApiService.parseBannerResponse(response.data.primary_banner);
            this.applicationStore.addToHomepagePrimaryBanner(banner);
          }

          if (!!response.data && !!response.data.secondary_banner) {
            const banner: Banner = ApiService.parseBannerResponse(response.data.secondary_banner);
            this.applicationStore.addToHomepageSecondaryBanner(banner);
          }
        })
        .catch((error: AxiosError<SearchResponseItem[]>) => {
          const response = error.response;

          // Log errors - Network error / CORS issue / backend error
          LogService.error(`getHomepageConfig response is ${response?.status || "null"}`);
        })
        .then(() => {
          LogService.info("getHomepageConfig request complete.");
          resolve();
        });
    });
  }

  private static _getHidePayWalledProfilesApiValue(hidePayWalledProfilesQuery: boolean | undefined): boolean {
    return (
      // Check if override is provided in search URL
      hidePayWalledProfilesQuery === true ||
      // fall back to user preference if URL override is not provided
      this.sessionStore.isHidePayWalledProfilesPreference
    );
  }

  private static _getSearchQueries(): VignetteQueryString[] {
    const searchQuery: SearchQuery = this.applicationStore.getSearchCurrentQuery;
    const _searchQuery: SearchQueryStrings = SearchService.generateSearchQueryStrings(searchQuery);
    const queryStrings: VignetteQueryString[] = [];

    queryStrings.push({
      queryString: VignetteQueryStrings.SEARCH_PAGE,
      value: String(_searchQuery.page > 0 ? _searchQuery.page - 1 : 0),
    });
    queryStrings.push({
      queryString: VignetteQueryStrings.SEARCH_PAGE_SIZE,
      value: String(_searchQuery.pageSize),
    });
    queryStrings.push({
      queryString: "order",
      value: _searchQuery.order || SearchOrder.RELEVANCE,
    });

    if (_searchQuery.q) {
      queryStrings.push({
        queryString: VignetteQueryStrings.SEARCH_Q,
        value: _searchQuery.q,
      });
    }
    if (_searchQuery.gender)
      queryStrings.push({
        queryString: "gender",
        value: _searchQuery.gender,
      });
    if (_searchQuery.political_party)
      queryStrings.push({
        queryString: "political_party",
        value: _searchQuery.political_party,
      });
    if (_searchQuery.religion)
      queryStrings.push({
        queryString: "religion",
        value: _searchQuery.religion,
      });
    if (_searchQuery.ethnicity)
      queryStrings.push({
        queryString: "ethnicity",
        value: _searchQuery.ethnicity,
      });
    if (_searchQuery.current_office_state_abbr)
      queryStrings.push({
        queryString: "current_office_state_abbr",
        value: _searchQuery.current_office_state_abbr,
      });
    if (_searchQuery.city)
      queryStrings.push({
        queryString: "city",
        value: _searchQuery.city,
      });
    if (_searchQuery.state)
      queryStrings.push({
        queryString: "state",
        value: _searchQuery.state,
      });
    if (_searchQuery.country)
      queryStrings.push({
        queryString: "country",
        value: _searchQuery.country,
      });
    if (_searchQuery.education)
      queryStrings.push({
        queryString: "education",
        value: _searchQuery.education,
      });
    if (_searchQuery.current_office_congressional_district)
      queryStrings.push({
        queryString: "current_office_congressional_district",
        value: _searchQuery.current_office_congressional_district,
      });
    if (_searchQuery.birth_year_selection)
      queryStrings.push({
        queryString: "birth_year",
        value: SearchHelpers.getBirthYearString(
          _searchQuery.birth_year_selection,
          "",
          _searchQuery.birth_date_operator
        ),
      });
    if (_searchQuery.age_selection)
      queryStrings.push({
        queryString: "age",
        value: SearchHelpers.getBirthYearString("", _searchQuery.age_selection, _searchQuery.birth_date_operator),
      });
    if (_searchQuery.sig_id) {
      queryStrings.push({
        queryString: "sig_expression",
        value: SearchHelpers.getSigExpressionString(
          _searchQuery.sig_id,
          _searchQuery.sig_operator,
          _searchQuery.sig_rating
        ),
      });
    }
    if (_searchQuery.editorial_group)
      queryStrings.push({
        queryString: "editorial_groups",
        value: _searchQuery.editorial_group,
      });
    if (_searchQuery.profile_types)
      queryStrings.push({
        queryString: "profile_types",
        value: _searchQuery.profile_types,
      });
    if (_searchQuery.key_policy_areas)
      queryStrings.push({
        queryString: "key_policy_areas",
        value: _searchQuery.key_policy_areas,
      });
    if (_searchQuery.industry)
      queryStrings.push({
        queryString: "industry",
        value: _searchQuery.industry,
      });
    if (_searchQuery.include_former)
      queryStrings.push({
        queryString: "include_former",
        value: String(_searchQuery.include_former),
      });
    if (_searchQuery.agency)
      queryStrings.push({
        queryString: "agencies",
        value: _searchQuery.agency,
      });
    if (_searchQuery.committees)
      queryStrings.push({
        queryString: "committees",
        value: _searchQuery.committees,
      });
    if (_searchQuery.caucuses)
      queryStrings.push({
        queryString: "caucuses",
        value: _searchQuery.caucuses,
      });

    if (this._getHidePayWalledProfilesApiValue(_searchQuery.hidePayWalledProfiles)) {
      queryStrings.push({
        queryString: "hide_paywalled_profiles",
        value: "true",
      });
    }

    return queryStrings;
  }

  // TODO: validate query
  static async getSearch(): Promise<void> {
    return new Promise<void>((resolve) => {
      // Check if page we need is cached locally
      const currentPageCache: SearchItem[] | null = this.applicationStore.getSearchCurrentPageItems;

      if (!currentPageCache?.length) {
        const queryStrings: VignetteQueryString[] = this._getSearchQueries();

        this.applicationStore.setSearchLoading(true);

        axios
          .get<SearchResponse>(this._getApiEndpoint(VignetteEndpoints.API_SEARCH, undefined, queryStrings))
          .then((response: AxiosResponse<SearchResponse>) => {
            LogService.debug(`getSearch response is 200.`);
            ApiService.updateSearchResults(response.data);
          })
          .catch((error: AxiosError<SearchResponse>) => {
            const response = error.response;

            // Log errors - Network error / CORS issue / backend error
            LogService.error(`getSearch response is ${response?.status || "null"}`);
          })
          .then(() => {
            LogService.info("getSearch request complete.");
            this.applicationStore.setSearchLoading(false);
            resolve();
          });
      } else {
        // Page is already cached, do nothing.
        resolve();
      }
    });
  }

  static async getAutoCompleteSearch(keyword: string, hidePayWalledProfilesQuery: boolean | undefined): Promise<void> {
    const queryStrings: VignetteQueryString[] = [];

    return new Promise<void>((resolve) => {
      queryStrings.push({
        queryString: VignetteQueryStrings.SEARCH_PAGE_SIZE,
        value: String(5),
      });
      queryStrings.push({
        queryString: VignetteQueryStrings.SEARCH_Q,
        value: keyword,
      });
      queryStrings.push({
        queryString: "order",
        value: SearchOrder.RELEVANCE,
      });
      queryStrings.push({
        queryString: VignetteQueryStrings.SEARCH_AUTOCOMPLETE,
        value: "true",
      });

      if (this._getHidePayWalledProfilesApiValue(hidePayWalledProfilesQuery)) {
        queryStrings.push({
          queryString: "hide_paywalled_profiles",
          value: "true",
        });
      }

      this.applicationStore.setAutoCompleteSearchLoading(true);

      axios
        .get<SearchResponse>(this._getApiEndpoint(VignetteEndpoints.API_SEARCH, undefined, queryStrings))
        .then((response: AxiosResponse<SearchResponse>) => {
          LogService.debug(`getAutoCompleteSearch response is 200.`);
          this.applicationStore.setAutoCompleteSearchResults(this.parseSearchResponseList(response.data.results));
        })
        .catch((error: AxiosError<SearchResponse>) => {
          const response = error.response;

          // Log errors - Network error / CORS issue / backend error
          LogService.error(`getAutoCompleteSearch response is ${response?.status || "null"}`);
        })
        .then(() => {
          LogService.info("getAutoCompleteSearch request complete.");
          this.applicationStore.setAutoCompleteSearchLoading(false);
          resolve();
        });
    });
  }

  private static parseSearchResponseItem(searchResponseItem: SearchResponseItem): SearchItem {
    return {
      subscriptionModules: searchResponseItem.subscription_modules || [],
      id: searchResponseItem.id || -1,
      briefcaseId: searchResponseItem.briefcase_item_id || -1,
      isFormer: searchResponseItem.is_former || false,
      positionSummary: searchResponseItem.position_summary || "",
      preferredNameDisplay: searchResponseItem.preferred_name_display || "",
      title: searchResponseItem.title || "",
      politicalParty: searchResponseItem.political_party || "",
      quickSummary: searchResponseItem.quick_summary || "",
      headShot: searchResponseItem.headshot || "",
      addedToBriefcase: this.applicationStore.isAddedToBriefcaseById(searchResponseItem.briefcase_item_id || -1),
    };
  }

  private static parseNoteworthyGroupResponseItem(groupResponseItem: NoteworthyGroupResponseItem): NoteworthyGroup {
    return {
      id: groupResponseItem.id || -1,
      name: groupResponseItem.name || "",
      description: groupResponseItem.description || "",
      order: groupResponseItem.order || Number.MAX_SAFE_INTEGER,
    };
  }

  private static parseBannerResponse(banner: BannerResponseItem): Banner {
    return {
      wideImg: banner.wide_img || "",
      smallImg: banner.small_img || "",
      url: banner.url || "",
    };
  }

  private static parseSearchResponseList(searchResponseList: SearchResponseItem[] | null): SearchItem[] {
    if (searchResponseList == null) {
      return [];
    } else {
      return searchResponseList.map((searchResponse) => {
        return this.parseSearchResponseItem(searchResponse);
      });
    }
  }

  // TODO: validate response?
  private static updateSearchResults(response: SearchResponse): void {
    const results: SearchItem[] = this.parseSearchResponseList(response.results);
    const searchPagination: SearchResultsPagination = {
      pageSize: response.pagination?.pageSize || 12,
      page: response.pagination?.page != null ? response.pagination.page + 1 : 1,
      totalPages: response.pagination?.totalPages != null ? response.pagination.totalPages + 1 : 1,
      totalHits: response.pagination?.totalHits || 0,
      order: response.pagination?.order === SearchOrder.ALPHABETICAL ? SearchOrder.ALPHABETICAL : SearchOrder.RELEVANCE,
    };

    // Make sure both setSearchResultsPagination and setSearchResults are called at the same time, and are in sync
    this.applicationStore.setSearchResultsPagination(searchPagination);
    this.applicationStore.setSearchResults(results);
  }

  // TODO: validate profileId
  static async getProfile(profileId: number): Promise<Profile | null> {
    let result: Profile | null = null;

    return new Promise<Profile | null>((resolve, reject) => {
      axios
        .get<ProfileResponse>(this._getApiEndpoint(VignetteEndpoints.API_PROFILE, String(profileId)))
        .then((response: AxiosResponse<ProfileResponse>) => {
          result = ApiService.parseProfileResponse(response.data);
          resolve(result);
        })
        .catch((error: AxiosError<ProfileResponse>) => {
          const response = error.response;

          // Log errors - Network error / CORS issue / backend error
          LogService.error(`getProfile response is ${response?.status || "null"}`);

          reject();
        })
        .then(() => {
          LogService.info("getProfile request complete.");
        });
    });
  }

  private static parseLocationResponseArray(responseArray: LocationResponse[] | null): string[] {
    if (responseArray) {
      return responseArray.map((locationResponse) => this.parseLocationResponse(locationResponse));
    }
    return [];
  }

  private static parseLocationResponse(response: LocationResponse | null): string {
    if (response == null || response.display == null) {
      return "";
    } else {
      return response.display.split(", United States of America")[0];
    }
  }

  private static parseFecDataResponse(response: FecDataResponse | null): FecData | null {
    return response && response.cid
      ? {
          cashOnHand: parseFloat(response.cash_on_hand),
          cid: response.cid,
          cycle: parseInt(response.cycle),
          debt: parseFloat(response.debt),
          lastUpdated: response.last_updated, // This could be a date object...
          spent: parseFloat(response.spent),
          total: parseFloat(response.total),
        }
      : null;
  }

  private static parseFieldFromLookupTable(field: string, lookupTable: LookupTableItem[]): string {
    if (field && lookupTable != null && lookupTable.length > 0) {
      const fieldParsed = lookupTable.find((entry) => entry.id === field);
      if (fieldParsed) {
        return fieldParsed.name;
      }
    }
    return field;
  }

  /**
   * If only the year is set, just display the year
   * If only the month and birthdate_year are set, display them as MMMM YYYY
   * If only the month and birthdate_day are set, display them as MMMM d
   * If all date components are set, display the date as MMMM d, YYYY
   * If it's birthday and year is set, after the birthdate display the age: (age 53)
   */
  private static parseDate(
    year: number | null,
    month: number | null,
    day: number | null,
    age: string | null = null
  ): string {
    // Check if we have any valid combination available.
    if (!year && !month && (!day || !year)) {
      LogService.debug(`ApiService: can't parse birthday: year:'${year}', month:'${month}', day:'${day}'`);
      return "";
    }

    const yearAndAge = year ? year + (age ? ` (age ${age})` : "") : "";

    if (!month) {
      return yearAndAge;
    } else {
      if (day) {
        return `${DateHelper.getMonthString(month)} ${day}${yearAndAge ? ", " + yearAndAge : ""}`;
      } else {
        return DateHelper.getMonthString(month) + " " + yearAndAge;
      }
    }
  }

  private static getDateObject(isoString: string): Date {
    return new Date(isoString);
  }

  private static parseIsoDate(dateString: string | null): string {
    if (!!dateString) {
      try {
        const date = this.getDateObject(dateString);
        return this.parseDate(date.getFullYear(), date.getMonth() + 1, date.getDate());
      } catch (e) {}
    }
    return "";
  }

  private static parseProfileResponse(response: ProfileResponse): Profile {
    return {
      headShot: response.headshot || "",
      preferredNameDisplay: response.preferred_name_display || "",
      positionSummary: response.position_summary || "",
      phoneticName: response.phonetic_name || "",
      primaryProfileType: this.parseFieldFromLookupTable(
        response.primary_profile_type || "",
        this.applicationStore.getLookupTable(LookupTablesKeys.PRIMARY_PROFILE_TYPES)
      ),
      primaryProfileTypesAdditional: !response.primary_profile_types_additional
        ? []
        : response.primary_profile_types_additional.map((profileType) =>
            this.parseFieldFromLookupTable(
              profileType || "",
              this.applicationStore.getLookupTable(LookupTablesKeys.PRIMARY_PROFILE_TYPES)
            )
          ),
      secondaryProfileTypes: !response.secondary_profile_types
        ? []
        : response.secondary_profile_types.map((profileType) =>
            this.parseFieldFromLookupTable(
              profileType || "",
              this.applicationStore.getLookupTable(LookupTablesKeys.SECONDARY_PROFILE_TYPES)
            )
          ),
      gender: response.gender || "",
      age: response.age || "",
      birthplace: this.parseLocationResponse(response.birthplace),
      hometown: this.parseLocationResponse(response.hometown),
      residences: this.parseLocationResponseArray(response.residences),
      religion: this.parseFieldFromLookupTable(
        response.religion || "",
        this.applicationStore.getLookupTable(LookupTablesKeys.RELIGIONS)
      ),
      ethnicity: this.parseFieldFromLookupTable(
        response.ethnicity || "",
        this.applicationStore.getLookupTable(LookupTablesKeys.ETHNICITIES)
      ),
      education: !response.education
        ? []
        : response.education.map((education) => {
            return {
              degree: this.parseFieldFromLookupTable(
                education.degree || "",
                this.applicationStore.getLookupTable(LookupTablesKeys.DEGREES)
              ),
              degreeIncomplete: education.degree_incomplete || false,
              fieldOfStudy: education.field_of_study || "",
              school: !education.school
                ? {
                    name: "",
                    type: "",
                    location: this.parseLocationResponse(null),
                  }
                : {
                    name: education.school.name || "",
                    type: education.school.type || "",
                    location: this.parseLocationResponse(education.school.location),
                  },
              school_department: education.school_department || "",
              yearStart: education.year_start || -1,
              yearEnd: education.year_end || -1,
            };
          }),
      educationSummary: response.education_summary || "", // HTML
      maritalStatus: this.parseFieldFromLookupTable(
        response.marital_status || "",
        this.applicationStore.getLookupTable(LookupTablesKeys.MARITAL_STATUSES)
      ),
      familyMembers: response.family_members || "", // HTML
      title: response.title || "",
      politicalParty: this.parseFieldFromLookupTable(
        response.political_party || response.political_party_other || "",
        this.applicationStore.getLookupTable(LookupTablesKeys.POLITICAL_PARTIES)
      ),
      electoralHistory: !response.electoral_history
        ? []
        : response.electoral_history.map((electoralHistory) => {
            return {
              year: electoralHistory.year || "",
              raceName: electoralHistory.race_name || "",
              status: electoralHistory.status || "",
            };
          }),
      workHistory: response.work_history || "", // HTML
      electoralOverview: response.electoral_overview || "", // HTML
      committees: !response.committees
        ? []
        : response.committees.map((committee: CommitteeResponse) => {
            return {
              committeeName: committee.committee_name || "",
              leadershipTitle: committee.leadership_title || "",
              subCommittees: !committee.sub_committees
                ? []
                : committee.sub_committees.map((subCommittee: SubCommitteeResponse) => {
                    return {
                      committeeName: subCommittee.committee_name || "",
                      leadershipTitle: subCommittee.leadership_title || "",
                    };
                  }),
            };
          }),
      caucuses: !response.caucuses
        ? []
        : response.caucuses.map((caucusCategory: CaucusCategoryResponse) => {
            return {
              caucusCategoryName: caucusCategory.category_name || "",
              caucuses: !caucusCategory.caucuses
                ? []
                : caucusCategory.caucuses.map((caucus: CaucusResponse) => {
                    return {
                      caucusName: caucus.caucus_name || "",
                      leadershipTitle: caucus.leadership_title || "",
                    };
                  }),
            };
          }),
      quickSummary: response.quick_summary || "", // HTML
      approachMotivations: response.approach_motivations || "", // HTML
      policyPositionsFocus: response.policy_positions_focus || "", // HTML
      coreCommunities: response.core_communities || "", // HTML
      politicalHistory: response.political_history || "", // HTML
      financialInfo: response.financial_info || "", // HTML
      publicationsMedia: response.publications_media || "", // HTML
      professionalAffiliations: response.professional_affiliations || "", // HTML
      awards: response.awards || "", // HTML
      familyPersonal: response.family_personal || "", // HTML
      criticismsControversies: response.criticisms_controversies || "", // HTML
      covid19Response: response.covid19_response || "", // HTML
      facebookUrl: response.facebook_url || "",
      twitterUsername: response.twitter_username || "",
      instagramUsername: response.instagram_username || "",
      linkedInUrl: response.linkedin_url || "",
      websiteUrl: response.website_url || "",
      almanacUrl: response.almanac_url || "",
      address: response.address || "",
      email: response.email || "",
      phone: response.phone || "",
      fecData: this.parseFecDataResponse(response.fec_data),
      keyVotes: response.key_votes.map((keyVote) => {
        return {
          billNumber: keyVote.bill_number,
          name: keyVote.name,
          vote: keyVote.vote,
        };
      }),

      sigRatings: response.sig_ratings.map((sigRating) => {
        return {
          name: sigRating.special_interest_group__name,
          rating: sigRating.rating + (Number.isInteger(Number.parseInt(sigRating.rating)) ? "%" : ""),
          timespan: sigRating.timespan,
        };
      }),

      relatedProfiles: response.related_profiles.map((relatedProfile) => {
        return {
          subscriptionModules: relatedProfile.subscription_modules || [],
          id: relatedProfile.id || -1,
          briefcaseId: relatedProfile.briefcase_item_id || -1,
          isFormer: relatedProfile.is_former || false,
          positionSummary: relatedProfile.position_summary || "",
          preferredNameDisplay: relatedProfile.preferred_name_display || "",
          title: relatedProfile.title || "",
          politicalParty: relatedProfile.political_party || "",
          quickSummary: relatedProfile.quick_summary || "",
          headShot: relatedProfile.headshot || "",
          addedToBriefcase: this.applicationStore.isAddedToBriefcaseById(relatedProfile.briefcase_item_id || -1),
        };
      }),

      id: response.id || 0,
      briefcaseId: response.briefcase_item_id || 0,
      docxUrl: response.docx_url || "",
      pdfUrl: response.pdf_url || "",
      createdDate: response.created_date ? new Date(response.created_date) : null, // ISO-Date
      isStub: response.is_stub || false,
      isFormer: response.is_former || false,
      subscriptionModules: response.subscription_modules || [],
      isUnlocked: response.is_unlocked || false,

      /* computed */
      birthDate: this.parseDate(
        response.birthdate_year,
        response.birthdate_month,
        response.birthdate_day,
        response.age || ""
      ),
      updatedDate: this.parseIsoDate(response.updated_date),
      substantiveUpdatedDate: this.parseIsoDate(response.substantive_updated_date),
      addedToBriefcase: this.applicationStore.isAddedToBriefcaseById(response.briefcase_item_id || -1), // computed
    };
  }

  // Returns true if there is a next page.
  private static async getBriefcase(pageNumber = 1): Promise<boolean> {
    const queryStrings: VignetteQueryString[] = [];

    queryStrings.push({
      queryString: VignetteQueryStrings.BRIEFCASE_PAGE,
      value: String(pageNumber),
    });

    try {
      const response: AxiosResponse<BriefcaseResponse> = await axios.get(
        this._getApiEndpoint(VignetteEndpoints.API_BRIEFCASE, undefined, queryStrings)
      );
      if (response.data != null && response.data.results != null) {
        const briefcaseFolders: BriefcaseFolder[] = response.data.results.map((briefcaseResponseFolder) => {
          return ApiService.parseBriefcaseResponseFolder(briefcaseResponseFolder);
        });

        briefcaseFolders.push({
          id: BriefcaseHardcodedIds.TOP_DIVIDER,
          isDefault: false,
          name: "",
          items: [],
          divider: true,
          dossierUrl: "",
        });
        briefcaseFolders.push({
          id: BriefcaseHardcodedIds.BOTTOM_DIVIDER,
          isDefault: false,
          name: "",
          items: [],
          divider: true,
          dossierUrl: "",
        });
        briefcaseFolders.push({
          id: BriefcaseHardcodedIds.NEW,
          isDefault: false,
          name: "Add new...",
          items: [],
          dossierUrl: "",
        });
        this.applicationStore.setBriefcaseFolders(briefcaseFolders);

        // return response.data.next != null && response.data.next != "";
      }
    } catch (e) {
      LogService.error(`Error when getting briefcase: ${JSON.stringify(e)}`);
    }
    return false;
  }

  // Refresh briefcase if more than 2 minutes has passed (or forced by flag), skip if refreshed more recently.
  static async refreshBriefcase(forceUpdate = false): Promise<boolean> {
    return new Promise<boolean>(async (resolve) => {
      const lastUpdated = this.applicationStore.getBriefcaseLastUpdated;
      const isAlreadyLoading = this.applicationStore.isBriefcaseLoading;
      const now = new Date();

      // Update briefcase only if it's been more than 2 minutes
      if (!isAlreadyLoading && (forceUpdate || lastUpdated == null || now.getTime() - lastUpdated.getTime() > 120000)) {
        LogService.info("Updating briefcase items.");

        this.applicationStore.setBriefcaseLoading(true);

        // let currentPage = 1;
        // let nextPage = true;
        // while (nextPage) {
        // nextPage = await this.getBriefcase(currentPage);
        // currentPage++;
        // }
        await this.getBriefcase();

        this.applicationStore.setBriefcaseLoading(false);
        this.applicationStore.setBriefcaseLastUpdated(new Date());

        // Update briefcase status of cached items
        this.applicationStore.refreshHomepageFeaturedProfilesBriefcaseStatus();
        this.applicationStore.refreshSearchResultProfilesBriefcaseStatus();
      } else if (isAlreadyLoading) {
        LogService.info(`Skipping Briefcase update: isAlreadyLoading`);
      } else {
        LogService.info(
          `Skipping Briefcase update: lastUpdated: ${lastUpdated ? lastUpdated.getTime() : "null"}, diff: ${
            lastUpdated ? now.getTime() - lastUpdated.getTime() : "null"
          }`
        );
      }
      resolve(true);
    });
  }

  private static parseBriefcaseResponseFolder(briefcaseResponseFolder: BriefcaseResponseFolder): BriefcaseFolder {
    return {
      id: briefcaseResponseFolder.id || -1,
      isDefault: briefcaseResponseFolder.is_default || false,
      name: briefcaseResponseFolder.name || "General Briefcase",
      items:
        briefcaseResponseFolder.items !== null
          ? briefcaseResponseFolder.items.map((briefcaseResponseItem) => {
              return ApiService.parseBriefcaseResponseItem(briefcaseResponseItem);
            })
          : [],
      dossierUrl: briefcaseResponseFolder.dossier_url || "#",
    };
  }

  private static parseBriefcaseResponseItem(briefcaseResponseItem: BriefcaseResponseItem): BriefcaseItem {
    return {
      subscriptionModules: briefcaseResponseItem.subscription_modules || [],
      id: briefcaseResponseItem.id || -1,
      briefcaseId: briefcaseResponseItem.briefcase_item_id || -1,
      positionSummary: briefcaseResponseItem.position_summary || "",
      preferredNameDisplay: briefcaseResponseItem.preferred_name_display || "",
      title: briefcaseResponseItem.title || "",
      politicalParty: briefcaseResponseItem.political_party || "",
      quickSummary: briefcaseResponseItem.quick_summary || "",
      headShot: briefcaseResponseItem.headshot || "",
      addedToBriefcase: true,
      isFormer: briefcaseResponseItem.is_former || false,
    };
  }

  static async postBriefcaseToggle(
    items: SearchItem[],
    action?: BriefcaseActions,
    sourceFolderId?: number,
    destinationFolderId?: number
  ): Promise<boolean> {
    if (
      action !== undefined &&
      action !== BriefcaseActions.ADD &&
      action !== BriefcaseActions.REMOVE &&
      action !== BriefcaseActions.MOVE
    ) {
      LogService.warn(`ApiService.postBriefcaseToggle invalid action: '${action}`);
      return false;
    }

    if (items.length === 0) {
      LogService.warn("ApiService.postBriefcaseToggle profile list is empty.");
      return false;
    }
    if (sourceFolderId === undefined) {
      if (action === BriefcaseActions.ADD) {
        sourceFolderId = this.applicationStore.getBriefcaseDefaultFolderId;
      } else {
        // action === BriefcaseActions.REMOVE || action === BriefcaseActions.MOVE
        sourceFolderId = this.applicationStore.getBriefcaseFolderIdForId(items[0].briefcaseId);

        if (sourceFolderId < 0) {
          LogService.warn("ApiService.postBriefcaseToggle items aren't added to a briefcase folder.");
          return false;
        }
      }
    }

    if (action === BriefcaseActions.MOVE) {
      if (destinationFolderId === undefined) {
        LogService.warn(`ApiService.postBriefcaseToggle destination folder needed for move operation.`);
        return false;
      }

      if (sourceFolderId === destinationFolderId) {
        LogService.warn(`ApiService.postBriefcaseToggle source and destination folder cannot be same.`);
        return false;
      }
    }
    // FIXME: check for error message
    const result = await ApiService.postBriefcaseToggleWithId(
      items.map((searchItem) => searchItem.briefcaseId),
      action,
      sourceFolderId,
      destinationFolderId
    );
    if (result) {
      if (action === BriefcaseActions.ADD) {
        this.applicationStore.addToBriefcase({ items, folderId: sourceFolderId });
      } else if (action === BriefcaseActions.REMOVE) {
        this.applicationStore.removeFromBriefcase({ items, folderId: sourceFolderId });
      } else if (action === BriefcaseActions.MOVE) {
        this.applicationStore.removeFromBriefcase({ items, folderId: sourceFolderId });
        this.applicationStore.addToBriefcase({ items, folderId: destinationFolderId });
      } else {
        LogService.warn(`ApiService.postBriefcaseToggleWithId invalid action: '${action}`);
      }
      this.applicationStore.refreshHomepageFeaturedProfilesBriefcaseStatus();
      this.applicationStore.refreshSearchResultProfilesBriefcaseStatus();
      this.sessionStore.refreshViewedProfilesBriefcaseStatus();
      return new Promise<boolean>((resolve) => {
        resolve(true);
      });
    }
    return new Promise<boolean>((resolve) => {
      resolve(false);
    });
  }

  // Does not validate arguments, make sure action/sourceFolderId/destinationFolderId are passed as needed
  private static async postBriefcaseToggleWithId(
    briefcaseItemIds: number[],
    action?: string,
    sourceFolderId?: number,
    destinationFolderId?: number
  ): Promise<boolean> {
    const requestBody: BriefcaseToggleRequest = {
      briefcase_item_ids: briefcaseItemIds,
    };
    let success = true;
    if (action !== undefined) {
      requestBody.action = action;
    }
    if (sourceFolderId !== undefined) {
      requestBody.folder_id = sourceFolderId;
    }
    if (destinationFolderId !== undefined) {
      requestBody.dest_folder_id = destinationFolderId;
    }
    return new Promise<boolean>((resolve) => {
      axios
        .post(this._getApiEndpoint(VignetteEndpoints.API_BRIEFCASE_TOGGLE), requestBody)
        .then((response: AxiosResponse) => {
          // FIXME: set response type
          success = !response.data.error_message;
          LogService.info(`Response: '${JSON.stringify(response.data || {})}'`);
        })
        .catch((error: AxiosError) => {
          // FIXME: set response type
          const response = error.response;

          // Log errors - Network error / CORS issue / backend error
          LogService.error(`postBriefcaseToggleWithId response is ${response?.status || "null"}`);
        })
        .then(() => {
          LogService.info("briefcase toggle request is complete.");
          ApiService.refreshBriefcase();
          resolve(success);
        });
    });
  }

  static getBriefcaseFolderDownloadCheck(
    folderId: number,
    datatype = "docx",
    downloadOrder = "alphabetical",
    selectedProfiles: number[] = [],
    email = false,
    useCustomLogo = false
  ): Promise<BriefcaseFolderDownloadCheckResponse> {
    // Conditionally add params depending on what is needed.
    const params = {
      datatype: datatype,
      order: downloadOrder,
      use_custom_logo: useCustomLogo,
      ...(email && { email: email }),
      ...(selectedProfiles.length > 0 && { p: String(selectedProfiles) }),
    };

    return new Promise<BriefcaseFolderDownloadCheckResponse>((resolve) => {
      let result: BriefcaseFolderDownloadCheckResponse = {
        profile_count: 0,
        eta: 2,
        short: true,
        started: false,
      };
      axios
        .get<BriefcaseFolderDownloadCheckResponse>(
          this._getApiEndpoint(VignetteEndpoints.API_BRIEFCASE_DOWNLOAD_CHECK) + folderId,
          { params }
        )
        .then((response: AxiosResponse<BriefcaseFolderDownloadCheckResponse>) => {
          result = response.data;
        })
        .catch((error: AxiosError<BriefcaseFolderDownloadCheckResponse>) => {
          // const response = error.response;
          // Log errors - Network error / CORS issue / backend error
          LogService.error(`postBriefcaseFolderAdd response is ${error.code || "null"}`);
          // }
        })
        .then(() => {
          resolve(result);
        });
    });
  }

  static async postBriefcaseFolderAdd(folder_name: string): Promise<BriefcaseFolderResponse> {
    const requestBody = {
      folder_name,
    };

    return new Promise<BriefcaseFolderResponse>((resolve) => {
      let result: BriefcaseFolderResponse = {
        success: null,
        folder_id: null,
        detail: "An error occurred, please try again.",
        statusCode: 500,
        dossier_url: "",
      };
      axios
        .post<BriefcaseFolderResponse>(this._getApiEndpoint(VignetteEndpoints.API_BRIEFCASE_FOLDER_ADD), requestBody)
        .then((response: AxiosResponse<BriefcaseFolderResponse>) => {
          result = response.data;
          result.statusCode = 200;

          this.applicationStore.addBriefcaseFolder({
            id: result.folder_id || 0,
            name: folder_name,
            isDefault: false,
            items: [],
            dossierUrl: result.dossier_url,
          });
        })
        .catch((error: AxiosError<BriefcaseFolderResponse>) => {
          const response = error.response;

          if (response != null) {
            if (response.status === 403) {
              result = response.data;
              result.statusCode = 403;
            }
            // Not logged in
            else if (response.status === 401) {
              result.statusCode = 401;
            }
            // else - result.statusCode remains 500 (set during initialization)

            // Log errors - Network error / CORS issue / backend error
            LogService.error(`postBriefcaseFolderAdd response is ${result.statusCode || "null"}`);
          }
        })
        .then(() => {
          LogService.info("postBriefcaseFolderAdd request is complete.");
          resolve(result);
        });
    });
  }

  static async postBriefcaseFolderDelete(folder_id: number): Promise<BriefcaseFolderResponse> {
    const requestBody = {
      folder_id,
    };

    return new Promise<BriefcaseFolderResponse>((resolve) => {
      let result: BriefcaseFolderResponse = {
        success: null,
        folder_id: null,
        detail: "An error occurred, please try again.",
        statusCode: 500,
        dossier_url: "",
      };
      axios
        .post<BriefcaseFolderResponse>(this._getApiEndpoint(VignetteEndpoints.API_BRIEFCASE_FOLDER_DELETE), requestBody)
        .then((response: AxiosResponse<BriefcaseFolderResponse>) => {
          result = response.data;
          result.statusCode = 200;

          const foldersToBeDeleted = this.applicationStore.getBriefcaseFolders.filter(
            (folder) => folder.id === folder_id
          );

          // Move any items to default folder
          if (
            foldersToBeDeleted != undefined &&
            foldersToBeDeleted.length > 0 &&
            foldersToBeDeleted[0].items.length > 0
          ) {
            const items: BriefcaseItem[] = foldersToBeDeleted[0].items;
            // TODO: move into applicationStore - either use context reference, or implement as Action
            this.applicationStore.removeFromBriefcase({
              items: items,
              folderId: folder_id,
            });
          }
          this.applicationStore.removeBriefcaseFolder(folder_id);
        })
        .catch((error: AxiosError<BriefcaseFolderResponse>) => {
          const response = error.response;

          if (response != null) {
            if (response.status === 403) {
              result = response.data;
              result.statusCode = 403;
            }
            // Not logged in
            else if (response.status === 401) {
              result.statusCode = 401;
            }
            // else - result.statusCode remains 500 (set during initialization)

            // Log errors - Network error / CORS issue / backend error
            LogService.error(`postBriefcaseFolderDelete response is ${result.statusCode || "null"}`);
          }
        })
        .then(() => {
          LogService.info("postBriefcaseFolderDelete request is complete.");
          resolve(result);
        });
    });
  }

  static async getEmailValidation(email: string): Promise<EmailVerificationResponse> {
    const queryStrings: VignetteQueryString[] = [];
    queryStrings.push({
      queryString: "email",
      value: email,
    });
    return new Promise<EmailVerificationResponse>((resolve) => {
      let result: EmailVerificationResponse = {
        status_msg: "",
        error_message: "An error occurred, please try again.",
        error_code: "",
        statusCode: 500,
      };
      axios
        .get<EmailVerificationResponse>(
          this._getApiEndpoint(VignetteEndpoints.API_EMAIL_VALIDATION, undefined, queryStrings)
        )
        .then((response: AxiosResponse<EmailVerificationResponse>) => {
          result = response.data;
          result.statusCode = 200;
        })
        .catch((error: AxiosError<EmailVerificationResponse>) => {
          const response = error.response;

          if (response != null) {
            if (response.status === 403) {
              result = response.data;
              result.statusCode = 403;
            }
            // Not logged in
            else if (response.status === 401) {
              result.statusCode = 401;
            }
            // else - result.statusCode remains 500 (set during initialization)
          }

          // Log errors - Network error / CORS issue / backend error
          LogService.error(`getEmailValidation response is ${response?.status || "null"}`);
        })
        .then(() => {
          LogService.info("emailVerification updated.");
          resolve(result);
        });
    });
  }

  static async getSession(): Promise<void> {
    return new Promise<void>((resolve) => {
      axios
        .get<SessionResponse>(this._getApiEndpoint(VignetteEndpoints.API_SESSION))
        .then((response: AxiosResponse<SessionResponse>) => {
          ApiService.updateUserSession(response.data, true);
        })
        .catch((error: AxiosError<SessionResponse>) => {
          const response = error.response;

          // Is logged in but doesn't have Vignette access
          if (response != null && response.status === 403) {
            ApiService.updateUserSession(response.data, false);
          }

          // Log errors - Network error / CORS issue / backend error
          if (response != null && (response.status === 401 || response.status === 403)) {
            LogService.warn(`getSession response is ${response?.status || "null"}`);
          } else {
            LogService.error(`getSession response is ${response?.status || "null"}`);
          }
        })
        .then(() => {
          LogService.info("session updated.");
          resolve();
        });
    });
  }

  private static updateUserSession(response: SessionResponse, hasVignetteAccess: boolean): void {
    const user = this.parseSessionResponse(response);
    this.sessionStore.setUser(user);
    this.sessionStore.setHasVignetteAccess(hasVignetteAccess);
  }

  private static parseSessionResponse(response: SessionResponse): User {
    return {
      firstName: response.first_name || "",
      lastName: response.last_name || "",
      // FIXME: make sure invalid string doesn't get saved like valid enum
      userType: response.user_type as UserType,
      username: response.username || "",
      email: response.email || "",
      phoneNumber: response.phone_number || "",
      accountName: response.account_name || "",
      salesforceContactId: response.salesforce_contact_id || "",
      salesforceId: response.salesforce_id || "",
      salesforceAccessString: this.parseSalesforceAccessString(response.salesforce_access_string || ""),
      subscriptionModules: response.subscription_modules || [],
      isVignetteOnly: response.vignette_only || false, // TODO: should this default to 'true'?
      isVignetteTrial: response.is_vignette_trial || false,
      isVignetteTrialActive: response.vignette_trial_active || false,
      isVignetteUserManager: response.is_vignette_user_manager || false,
      isAssignedUser: response.assigned_user || false,
      accountId: response.account_id || "",
      accountExpiration: this.getDateObject(response.expiration || ""),
      accountExpirationStr: this.parseIsoDate(response.expiration || ""),
      accountOrganization: {
        salesforceAccountId: response.account_organization.salesforce_account_id || "",
        salesforceAccountName: response.account_organization.salesforce_account_name || "",
        vignetteProductName: response.account_organization.vignette_product_name || "",
        profileRequestsAllowed: response.account_organization.vignette_profile_requests_allowed || 0,
        profileRequestsPending: response.account_organization.vignette_profile_requests_pending || 0,
        profileRequestsCompleted: response.account_organization.vignette_profile_requests_completed || 0,
        profileRequestsAddonsAllowed: response.account_organization.vignette_profile_requests_addons_allowed || 0,
        profileRequestsAddonsPending: response.account_organization.vignette_profile_requests_addons_pending || 0,
        profileRequestsAddonsCompleted: response.account_organization.vignette_profile_requests_addons_completed || 0,
        seatsAllowed: response.account_organization.vignette_seats_allowed || undefined, // optional
        assignedUsersList: response.account_organization.vignette_assigned_users || undefined, // optional
        hasUnlimitedSeats: response.account_organization.vignette_seats_unlimited || false,
        hasCustomLogo: response.account_organization.has_custom_logo || false,
      },
      isSuperUser: response.is_superuser || false,
      isStaff: response.is_staff || false,
      hidePayWalledProfilesPreference: response.hide_paywalled_profiles_preference || false,
      profilesViewedDuringTrial: response.vignette_profiles_viewed || [],
      profilesViewedDuringTrialItems: [],
    };
  }

  // TODO: make sure validate input, test for null/""/";"/"valid:2020-01-01"/"valid:2020-01-01;"
  private static parseSalesforceAccessString(accessString: string): SalesforcePermission[] {
    const salesforcePermissions: string[] = accessString.split(";");
    const result: SalesforcePermission[] = [];
    for (const permissionTuple of salesforcePermissions) {
      const permission: string[] = permissionTuple.split(":");
      result.push({ codename: permission[0], expiration: permission[1] });
    }
    return result;
  }

  static async getUserManagement(): Promise<string> {
    return new Promise<string>((resolve) => {
      let responseStatusMsg = "";
      axios
        .get<UserManagementResponse>(this._getApiEndpoint(VignetteEndpoints.API_USER_MANAGEMENT))
        .then((response: AxiosResponse<UserManagementResponse>) => {
          LogService.info(`getUserManagement response: '${response.data}'`);
          ApiService.updateAssignedUsersList(response.data);
          responseStatusMsg = response.data.status_msg || "";
        })
        .catch((error: AxiosError<UserManagementResponse>) => {
          const response = error.response;
          responseStatusMsg = response?.data.error_message || "Error: please try again.";

          // If call fails, user is probably inactive or has lapsed membership
          ApiService.disableUserManagement();

          // Log errors - Network error / CORS issue / backend error
          LogService.error(`getUserManagement response is ${response?.status || "null"}`);
        })
        .then(() => {
          LogService.info("getUserManagement is complete.");
          resolve(responseStatusMsg);
        });
    });
  }

  static async putUserManagement(userEmail: string, action?: string): Promise<string> {
    const formData = new FormData();
    formData.append("email", userEmail);
    if (action != null && action !== "") {
      formData.append("action", action);
    }

    return new Promise<string>((resolve) => {
      let responseStatusMsg = "";
      axios
        .put<UserManagementResponse>(this._getApiEndpoint(VignetteEndpoints.API_USER_MANAGEMENT), formData)
        .then((response: AxiosResponse<UserManagementResponse>) => {
          LogService.info(`putUserManagement response: '${response.data}'`);
          responseStatusMsg = response.data.status_msg || "";
          ApiService.updateAssignedUsersList(response.data);
        })
        .catch((error: AxiosError<UserManagementResponse>) => {
          const response = error.response;
          responseStatusMsg = response?.data.error_message || "Error: please try again.";

          // Log errors - Network error / CORS issue / backend error
          LogService.error(`putUserManagement response is ${response?.status || "null"}`);
        })
        .then(() => {
          LogService.info("putUserManagement request is complete.");
          resolve(responseStatusMsg);
        });
    });
  }

  private static disableUserManagement(): void {
    this.sessionStore.setVignetteUserManagement(false);
  }

  private static updateAssignedUsersList(response: UserManagementResponse): void {
    if (response == null || response.vignette_assigned_users == null || response.vignette_assigned_users.length === 0) {
      LogService.warn(`An invalid UserManagementResponse has been returned from getUserManagement endpoint`);
    } else {
      this.applicationStore.setAssignedUsersList(response.vignette_assigned_users);
    }
  }

  static async getAppConfig(): Promise<void> {
    return new Promise<void>((resolve) => {
      axios
        .get<AppConfigResponse>(this._getApiEndpoint(VignetteEndpoints.API_APP_CONFIG))
        .then((response: AxiosResponse<AppConfigResponse>) => {
          ApiService.updateAppConfig(response.data);
        })
        .catch((error: AxiosError<AppConfigResponse>) => {
          LogService.error(`getAppConfig error is ${error}`);
          const response = error.response;

          // Log errors - Network error / CORS issue / backend error
          LogService.error(`getAppConfig response is ${response?.status || "null"}`);
        })
        .then(() => {
          LogService.info("getAppConfig request is complete");
          resolve();
        });
    });
  }

  private static updateAppConfig(response: AppConfigResponse): void {
    if (
      response.lists == null ||
      response.lists.subscription_modules == null ||
      response.lists.subscription_modules.length === 0
    ) {
      LogService.warn(`An invalid SubscriptionModulesList has been returned from appConfig endpoint`);
    }

    const lookupTables: LookupTables = this.applicationStore.getDefaultLookupTables;
    const nestedLookupTables: NestedLookupTables = this.applicationStore.getDefaultNestedLookupTables;

    // Initialize from response, TODO: make sure we handle null cases
    lookupTables.subscriptionModules = response.lists.subscription_modules.map<SubscriptionModule>(
      (module: SubscriptionModuleResponse) => ({ id: module.id, name: module.name, abbreviation: module.unique_abbr })
    );
    lookupTables.editorialGroups = response.lists.editorial_groups.map<LookupTableItem>((item) =>
      ApiService.mapAppConfigNumberResponseToLookupTableItem(item)
    );
    lookupTables.states = response.lists.states;
    lookupTables.countries = response.lists.countries;
    lookupTables.positionStatuses = response.lists.position_statuses;
    lookupTables.genders = response.lists.genders;
    lookupTables.politicalParties = response.lists.political_parties;
    lookupTables.maritalStatuses = response.lists.marital_statuses;
    lookupTables.ethnicities = response.lists.ethnicities;
    lookupTables.religions = response.lists.religions;
    // lookupTables.industries = response.lists.industries;
    lookupTables.degrees = response.lists.degrees;
    lookupTables.districts = response.lists.districts;

    nestedLookupTables.profileTypes = response.lists.profile_types.map<NestedLookupTableItem>(
      (item: AppConfigNestedNumberResponse) => this.mapAppConfigNestedNumberResponseToNestedLookupTableItem(item)
    );
    nestedLookupTables.keyPolicyAreas = response.lists.key_policy_areas.map<NestedLookupTableItem>(
      (item: AppConfigNestedNumberResponse) => this.mapAppConfigNestedNumberResponseToNestedLookupTableItem(item)
    );
    nestedLookupTables.industries = response.lists.industries.map<NestedLookupTableItem>(
      (item: AppConfigNestedNumberResponse) => this.mapAppConfigNestedNumberResponseToNestedLookupTableItem(item)
    );
    nestedLookupTables.committees = response.lists.committees.map<NestedLookupTableItem>(
      (item: AppConfigNestedNumberResponse) => this.mapAppConfigNestedNumberResponseToNestedLookupTableItem(item)
    );
    nestedLookupTables.caucuses = response.lists.caucuses.map<NestedLookupTableItem>(
      (item: AppConfigNestedNumberResponse) => this.mapAppConfigNestedNumberResponseToNestedLookupTableItem(item)
    );
    nestedLookupTables.agencies = response.lists.agencies.map<NestedLookupTableItem>(
      (item: AppConfigNestedNumberResponse) => this.mapAppConfigNestedNumberResponseToNestedLookupTableItem(item)
    );
    nestedLookupTables.specialInterestGroups = response.lists.special_interest_groups.map<NestedLookupTableItem>(
      (item: AppConfigNestedNumberResponse) => this.mapAppConfigNestedNumberResponseToNestedLookupTableItem(item)
    );

    this.applicationStore.setLookupTables(lookupTables);
    this.applicationStore.setNestedLookupTables(nestedLookupTables);
    // Compute generated fields
    this.applicationStore.setLookupTablesGeneratedFields();
    this.applicationStore.setUnlockedProfiles(response.lists.unlocked_profiles);
  }

  private static mapAppConfigNumberResponseToLookupTableItem(response: AppConfigNumberResponse): LookupTableItem {
    const lookupTableItem: LookupTableItem = {
      id: String(response.id),
      name: response.name,
    };

    if (response.description) lookupTableItem.description = response.description;
    return lookupTableItem;
  }

  private static mapAppConfigNestedNumberResponseToNestedLookupTableItem(
    response: AppConfigNestedNumberResponse
  ): NestedLookupTableItem {
    const result: NestedLookupTableItem = this.mapAppConfigNumberResponseToLookupTableItem(response);
    if (response.children != null && response.children.length > 0) {
      result.children = response.children.map<LookupTableItem>((item) =>
        this.mapAppConfigNumberResponseToLookupTableItem(item)
      );
    }
    return result;
  }

  static async postToggleHidePayWalledProfilesPreference(): Promise<void> {
    let result: boolean;
    return new Promise<void>((resolve) => {
      axios
        .post<HidePaywalledProfilesPreferenceResponse>(
          this._getApiEndpoint(VignetteEndpoints.API_TOGGLE_HIDE_PAY_WALLED_PROFILES_PREFERENCE)
        )
        .then((response: AxiosResponse<HidePaywalledProfilesPreferenceResponse>) => {
          LogService.info(`Response: '${response.data.hide_paywalled_profiles_preference}'`);
          result = response.data != undefined ? response.data.hide_paywalled_profiles_preference : false;

          if (result !== null) {
            this.sessionStore.setHidePayWalledProfilesPreference(result);
            SnackbarService.displaySnackbar("Successfully updated preferences.", "secondary");
          }
        })
        .catch((error: AxiosError) => {
          // FIXME: set response type
          const response = error.response;

          // Log errors - Network error / CORS issue / backend error
          LogService.error(`postToggleHidePayWalledProfilesPreference response is ${response?.status || "null"}`);
          SnackbarService.displaySnackbar("Failed to save preference changes.");
        })
        .then(() => {
          LogService.info("postToggleHidePayWalledProfilesPreference request is complete.");
          // ApiService.refreshBriefcase();
          resolve();
        });
    });
  }

  static async postUserContact(message: string, request_custom_logo: boolean): Promise<UserContactResponse> {
    const requestBody = {
      message,
      request_custom_logo,
    };

    return new Promise<UserContactResponse>((resolve) => {
      let result: UserContactResponse = {
        status_msg: "",
        error_message: "An error occurred, please try again.",
        statusCode: 500,
      };
      axios
        .post<UserContactResponse>(this._getApiEndpoint(VignetteEndpoints.API_USER_CONTACT), requestBody)
        .then((response: AxiosResponse<UserContactResponse>) => {
          result = response.data;
          result.statusCode = 200;
        })
        .catch((error: AxiosError<UserContactResponse>) => {
          const response = error.response;

          if (response != null) {
            if (response.status === 403) {
              result = response.data;
              result.statusCode = 403;
            }
            // Not logged in
            else if (response.status === 401) {
              result.statusCode = 401;
            }
            // else - result.statusCode remains 500 (set during initialization)

            // Log errors - Network error / CORS issue / backend error
            LogService.error(`postUserContact response is ${result.statusCode || "null"}`);
          }
        })
        .then(() => {
          LogService.info("postUserContact request is complete.");
          resolve(result);
        });
    });
  }

  static async postTrialSignup(
    email_address: string,
    first_name: string,
    last_name: string,
    phone_number: string,
    organization_name: string
  ): Promise<TrialSignupResponse> {
    const requestBody = {
      email_address,
      first_name,
      last_name,
      phone_number,
      organization_name,
    };

    return new Promise<TrialSignupResponse>((resolve) => {
      let result: TrialSignupResponse = {
        status_msg: "",
        error_message: "An error occurred, please try again.",
        statusCode: 500,
      };
      axios
        .post<EmailVerificationResponse>(this._getApiEndpoint(VignetteEndpoints.API_TRIAL_SIGNUP), requestBody)
        .then((response: AxiosResponse<EmailVerificationResponse>) => {
          result = response.data;
          result.statusCode = 200;
        })
        .catch((error: AxiosError<EmailVerificationResponse>) => {
          const response = error.response;

          if (response != null) {
            if (response.status === 403) {
              result = response.data;
              result.statusCode = 403;
            }
            // Not logged in
            else if (response.status === 401) {
              result.statusCode = 401;
            }
            // else - result.statusCode remains 500 (set during initialization)

            // Log errors - Network error / CORS issue / backend error
            LogService.error(`postTrialSignup response is ${result.statusCode || "null"}`);
          }
        })
        .then(() => {
          LogService.info("postTrialSignup request is complete.");
          resolve(result);
        });
    });
  }

  static async postResetPassword(email: string): Promise<TrialSignupResponse> {
    const requestBody = {
      email,
    };

    return new Promise<TrialSignupResponse>((resolve) => {
      let result: TrialSignupResponse = {
        status_msg: "",
        error_message: "An error occurred, please try again.",
        statusCode: 500,
      };
      axios
        .post<EmailVerificationResponse>(this._getApiEndpoint(VignetteEndpoints.API_RESET_PASSWORD), requestBody)
        .then((response: AxiosResponse<EmailVerificationResponse>) => {
          result = response.data;
          result.statusCode = 200;
        })
        .catch((error: AxiosError<EmailVerificationResponse>) => {
          const response = error.response;

          if (response != null) {
            if (response.status === 403) {
              result = response.data;
              result.statusCode = 403;
            }
            // Not logged in
            else if (response.status === 401) {
              result.statusCode = 401;
            }
            // else - result.statusCode remains 500 (set during initialization)

            // Log errors - Network error / CORS issue / backend error
            LogService.error(`postResetPassword response is ${result.statusCode || "null"}`);
          }
        })
        .then(() => {
          LogService.info("postResetPassword request is complete.");
          resolve(result);
        });
    });
  }
}
